/**
 * THE phone (<md) edge-to-edge rule (owner, 2026-10-03: the Breakdown table
 * "spans full page width", then "same for the Orders pages" — one rule, never
 * a per-page copy).
 *
 * `mx-[calc(50%_-_50vw)]` is a FULL-BLEED margin: it pulls the element out to
 * the viewport edges whatever the page's own side padding is (the order pages
 * use `p-2`, To Approve and the hires pages `p-2.5`), so no page has to know
 * its padding. It is idempotent: on an element whose container already spans
 * the viewport (a table inside a full-bleed Breakdown group card) it resolves
 * to 0. 🪤 Valid only where the container is centred in the viewport with
 * symmetric padding — true of every page body here; do not use it inside an
 * off-centre panel.
 *
 * `max-md:` only — desktop is untouched.
 */

/**
 * 📱 TIGHT PHONE TABLE CELLS (owner, 2026-10-03: "gaps between columns can be
 * reduced … reducing padding will work across columns", common to every
 * page). `index.css` (`.pst-table`, max-width 767px) drops every phone table
 * cell's side padding from 8px to 4px except the FIRST column (the PO/Order
 * or Gag cell keeps its own measured padding). The column widths below come
 * down by the same 8px so the space is actually given back, not left as a
 * wider blank at the end of each cell. Applied by every phone column builder
 * — never adjust one page's widths by hand.
 */
export const PHONE_CELL_PAD_SAVING = 8;
// The first column and any label-less control column (a kebab) keep their size.
export const tightenPhoneColumns = (columns) => columns.map((c, i) => (i === 0 || !c.label ? c : Object.freeze({
  ...c,
  width: c.width - PHONE_CELL_PAD_SAVING,
  ...(Number.isFinite(c.min) ? { min: c.min - PHONE_CELL_PAD_SAVING } : {}),
})));

/**
 * 📱 FLOATING ITEMS inside an open section (owner, 2026-10-03: gags and tasks
 * "floating" with a small gap). The section body is a light tinted well; each
 * item is its own white card. One pair for every phone grouped list (My Tasks
 * task groups, the Unassigned gag groups) so they cannot drift.
 */
// 🔀 Second pass the same day (owner: "remove the container around the
// groupings so each gag floats without a container"): NO group card — the
// header is its own floating card, the items float beneath it.
export const PHONE_GROUP_WRAPPER = '';
export const PHONE_GROUP_HEADER = 'rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden';
export const PHONE_SECTION_BODY = 'space-y-1 pt-1';
export const PHONE_FLOAT_ITEM = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden';

/** A ROW (toolbar, tab row): out to the screen edges. */
export const PHONE_FULL_BLEED = 'max-md:mx-[calc(50%_-_50vw)]';

/** A bordered, rounded CONTAINER (table shell, group card): edges, no side
    borders, no rounding — a card that touches the screen edge must not show
    a sliver of border or a clipped corner. */
export const PHONE_FULL_BLEED_CARD = `${PHONE_FULL_BLEED} max-md:border-x-0 max-md:rounded-none`;
